import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const PortfolioSection = styled.section`
  padding: 6rem 2rem;
  background: ${(props) => props.theme.colors.gray.lightest};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  color: ${(props) => props.theme.colors.secondary};
  text-align: center;
  margin-bottom: 3rem;
`;

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    align-items: center;
  }
`;

const FilterButton = styled(motion.button)<{ isActive: boolean }>`
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  background: ${(props) =>
    props.isActive ? props.theme.colors.secondary : 'transparent'};
  color: ${(props) =>
    props.isActive ? props.theme.colors.white : props.theme.colors.secondary};
  border: 2px solid ${(props) => props.theme.colors.secondary};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: '1 1 auto',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  maxWidth: '150px',

  &:hover {
    background: ${(props) => props.theme.colors.secondary};
    color: ${(props) => props.theme.colors.white};
  }
`;

const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  padding: 1rem;
`;

const ProjectCard = styled(motion.div)`
  background: ${(props) => props.theme.colors.white};
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 7px rgba(0, 0, 0, 0.35);
`;

const ProjectImage = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProjectInfo = styled.div`
  padding: 1.5rem;
`;

const ProjectTitle = styled.a`
  font-size: 1.2rem;
  color: ${(props) => props.theme.colors.secondary};
  margin-bottom: 0.5rem;
  font-weight: 600;
  text-decoration: underline;
  &:hover {
    text-decoration: none;
    color: ${(props) => props.theme.colors.primary};
  }
`;

const ProjectDescription = styled.p`
  color: ${(props) => props.theme.colors.gray.dark};
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const ProjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Tag = styled.span`
  padding: 0.2rem 0.8rem;
  background: ${(props) => props.theme.colors.primary}20;
  color: ${(props) => props.theme.colors.secondary};
  border-radius: 15px;
  font-size: 0.8rem;
`;

const projects = [
  {
    id: 1,
    title: 'Expense Review',
    description: 'A web application for tracking and reviewing expenses',
    image: './img/portfolio/expense-review.png',
    category: 'FE-development',
    url: 'https://www.zarazamanian.com/demo/expense-review/',
    tags: ['JS', 'HTML', 'CSS'],
  },
  {
    id: 2,
    title: 'Track Your Order',
    description: 'An interactive order tracking system for e-commerce',
    image: './img/portfolio/track-order.png',
    category: 'FE-development',
    url: 'https://www.zarazamanian.com/demo/track-your-order/',
    tags: ['JS', 'HTML', 'CSS', 'Illustration', 'UX/UI'],
  },
  {
    id: 3,
    title: 'Breakout Game',
    description: 'A classic arcade game built with HTML5 and JavaScript',
    image: './img/portfolio/breakout-game.png',
    category: 'FE-development',
    url: 'https://www.zarazamanian.com/demo/breakout-game/',
    tags: ['JS', 'HTML', 'CSS'],
  },
  {
    id: 4,
    title: 'Guess Word',
    description: 'A fun word guessing game with a fun storytelling twist',
    image: './img/portfolio/guess-word.png',
    category: 'FE-development',
    url: 'https://www.zarazamanian.com/demo/guess-word/',
    tags: ['JS', 'HTML', 'CSS', 'Illustration'],
  },
  {
    id: 5,
    title: 'Lyric Search',
    description: 'A web application for searching and displaying song lyrics',
    image: './img/portfolio/lyric-search.png',
    category: 'FE-development',
    url: 'https://www.zarazamanian.com/demo/lyric-search/',
    tags: ['API', 'JS', 'HTML', 'CSS', 'UX/UI'],
  },
];

const Portfolio: React.FC = () => {
  const [filter, setFilter] = useState<'all' | 'FE-development' | 'art'>('all');

  const filteredProjects = projects.filter((project) =>
    filter === 'all' ? true : project.category === filter
  );

  return (
    <PortfolioSection id='portfolio'>
      <Container>
        <Title
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          My Portfolio (WIP)
        </Title>

        <FilterContainer>
          {['all', 'FE-development', 'architecture', 'art'].map((category) => (
            <FilterButton
              key={category}
              isActive={filter === category}
              onClick={() => setFilter(category as typeof filter)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </FilterButton>
          ))}
        </FilterContainer>

        <Grid layout>
          <AnimatePresence>
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                <ProjectImage>
                  <img src={project.image} alt={project.title} />
                </ProjectImage>
                <ProjectInfo>
                  <ProjectTitle href={project.url} target='_blank'>
                    {project.title}
                  </ProjectTitle>
                  <ProjectDescription>{project.description}</ProjectDescription>
                  <ProjectTags>
                    {project.tags.map((tag, index) => (
                      <Tag key={index}>{tag}</Tag>
                    ))}
                  </ProjectTags>
                </ProjectInfo>
              </ProjectCard>
            ))}
          </AnimatePresence>
        </Grid>
      </Container>
    </PortfolioSection>
  );
};

export default Portfolio;
